import { Box, Grid, Stack } from '@atgfr8/atg-ui-tailwind';
import { BasicTable } from '@components';
import { AtgLoader } from '@components/AtgLoader';
import { getPortals } from '@store/customers';
import {
  customerDetailsStore,
  setCustomerDetailsStore,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { formatAmount, setSameAsOtherAddress } from '@utils/utils';
import { get } from 'lodash';
import { onMount, Show } from 'solid-js';

import {
  AdditionalCustomerDetails,
  CreditTaxEuler,
  GraphContainer,
  Insurance,
  Invoicing,
  MailingOrPhysicalAddress,
  OrderSummary,
  OutstandingInvoices,
  RequiredDocumentType,
} from './components';

type AvgDaysToPay = {
  name: string;
  last1Month: number | null;
  last3Months: number | null;
  last6Months: number | null;
  last12Months: number | null;
  type?: 'currency' | 'percent' | 'base';
};

type FieldT = 'last1Month' | 'last3Months' | 'last6Months' | 'last12Months';

const Financials = () => {
  const sameAsOthersAddress = (sourceType: 'mailing' | 'physical') => {
    setSameAsOtherAddress(
      sourceType,
      customerDetailsStore,
      updateCustomerDetailsState,
    );
  };

  const cellRendererFactory = (field: FieldT) => {
    return (value: AvgDaysToPay) => {
      let val = get(value, field, 'N/A');

      if (value.type === 'currency') {
        val = formatAmount(value[field] ?? 0);
      } else if (value.type === 'percent') {
        val = `${(value[field] ?? 0).toFixed(2)}%`;
      }

      return <>{val}</>;
    };
  };

  const columns = [
    {
      name: '',
      key: 'name',
      headerClasses: 'font-bold!',
      renderCell: (value: AvgDaysToPay) => {
        return <span class="font-bold! text-[#123B50]">{value.name}</span>;
      },
    },
    {
      name: 'Last 1 Month',
      key: 'last1Month',
      headerClasses: 'font-bold!',
      renderCell: cellRendererFactory('last1Month'),
    },
    {
      name: 'Last 3 Months',
      key: 'last3Months',
      headerClasses: 'font-bold!',
      renderCell: cellRendererFactory('last3Months'),
    },
    {
      name: 'Last 6 Months',
      key: 'last6Months',
      headerClasses: 'font-bold!',
      renderCell: cellRendererFactory('last6Months'),
    },
    {
      name: 'Last 12 Months',
      key: 'last12Months',
      headerClasses: 'font-bold!',
      renderCell: cellRendererFactory('last12Months'),
    },
  ];

  onMount(() => {
    getPortals()
      .then((portals) => {
        setCustomerDetailsStore('portals', portals);
      })
      .catch(() => {
        // Handle error if needed
      });
  });

  return (
    <Box class="h-full">
      <Show when={customerDetailsStore.isLoading}>
        <AtgLoader />
      </Show>
      <GraphContainer />
      <BasicTable
        tableContainerClasses="shadow-md! h-fit!"
        tableHeaderClasses="bg-[#ECEFF1]! "
        cellClasses="p-2! px-3!"
        columns={columns}
        rows={[
          {
            name: 'Avg. Days To Pay',
            last1Month:
              customerDetailsStore.customer.financials?.avgDaysToPayOneMonth ??
              0,
            last3Months:
              customerDetailsStore.customer.financials
                ?.avgDaysToPayThreeMonths ?? 0,
            last6Months:
              customerDetailsStore.customer.financials?.avgDaysToPaySixMonths ??
              0,
            last12Months:
              customerDetailsStore.customer.financials
                ?.avgDaysToPayTwelveMonths ?? 0,
          },
          {
            name: 'Avg. Profit',
            last1Month:
              customerDetailsStore.customer.financials?.avgProfit1M ?? 0,
            last3Months:
              customerDetailsStore.customer.financials?.avgProfit3M ?? 0,
            last6Months:
              customerDetailsStore.customer.financials?.avgProfit6M ?? 0,
            last12Months:
              customerDetailsStore.customer.financials?.avgProfit12M ?? 0,
            type: 'currency',
          },
          {
            name: 'Avg. Margin',
            last1Month:
              (customerDetailsStore.customer.financials?.avgMargin1M ?? 0) *
              100,
            last3Months:
              (customerDetailsStore.customer.financials?.avgMargin3M ?? 0) *
              100,
            last6Months:
              (customerDetailsStore.customer.financials?.avgMargin6M ?? 0) *
              100,
            last12Months:
              (customerDetailsStore.customer.financials?.avgMargin12M ?? 0) *
              100,
            type: 'percent',
          },
        ]}
      />
      <Grid container class="mt-2 w-full" columnSpacing={2}>
        <Grid item size={8}>
          <CreditTaxEuler />
          <Grid item>
            <Insurance />
          </Grid>
          <Grid item>
            <Invoicing />
          </Grid>
          <Grid item>
            <Stack spacing={2} direction="row">
              <MailingOrPhysicalAddress
                addressType="mailing"
                buttonLabel="SAME AS PHYSICAL"
                setSameAsOther={() => sameAsOthersAddress('physical')}
                customerStore={customerDetailsStore}
                customerFormState={updateCustomerDetailsState}
              />
              <MailingOrPhysicalAddress
                addressType="physical"
                buttonLabel="SAME AS MAILING"
                setSameAsOther={() => sameAsOthersAddress('mailing')}
                customerStore={customerDetailsStore}
                customerFormState={updateCustomerDetailsState}
              />
            </Stack>
          </Grid>
          <RequiredDocumentType />
        </Grid>
        <Grid item size={4}>
          <OrderSummary />
          <OutstandingInvoices />
          <AdditionalCustomerDetails />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Financials;
